/**
 * FR-017 — how a link row is told apart from its siblings.
 *
 * One document can hold several links to one object that share a type: two
 * `quantity` rows on different lines are different rows (the anchor is part
 * of the link id), and a farmer removing one must be able to see which. The
 * type alone is not enough (codex, FR-017 #3b), so every surface that lists
 * removable links renders these details beside it — in the active language.
 */
import i18n from '../../i18n';
import type { DocumentLink } from '../../services/twinService';
import { lineNumber, parseLineNumber } from './lineRows';
import { displayLocale, formatDay } from './linkTargetDisplay';

const CURRENCY_SIGNS: Readonly<Record<string, string>> = { EUR: '€' };
function localNumber(raw: unknown, minFraction: number): string | null {
  const canonical = parseLineNumber(raw);
  if (canonical === null) return null;
  return new Intl.NumberFormat(displayLocale(), { minimumFractionDigits: minFraction, maximumFractionDigits: 4 }).format(
    Number(canonical),
  );
}

/**
 * A line link's value in the active language — "4,20 €/kg" / "4.20 €/kg" for a
 * price, "120 kg Zugang" / "120 kg Incoming" for a quantity (FR-017 #5); `null`
 * for any other type or an unreadable value.
 */
export function linkValueLabel(link: DocumentLink): string | null {
  const p = link.payload ?? {};
  const unit = typeof p.unit === 'string' ? p.unit.trim() : '';
  if (link.link_type === 'price') {
    const price = localNumber(p.unit_price, 2);
    if (price === null) return null;
    const code = typeof p.currency === 'string' ? p.currency.trim().toUpperCase() : '';
    const money = [price, CURRENCY_SIGNS[code] ?? code].filter(Boolean).join(' ');
    return unit ? `${money}/${unit}` : money;
  }
  if (link.link_type === 'quantity') {
    const qty = localNumber(p.quantity, 0);
    if (qty === null) return null;
    const direction =
      p.direction === 'in' || p.direction === 'out' ? i18n.t(`twinLinks.details.${p.direction}`) : undefined;
    return [qty, unit, direction].filter(Boolean).join(' ');
  }
  return null;
}

/** A validity link's `valid_until` as a date in the active language; `null` for anything else. */
export function validUntil(link: DocumentLink): string | null {
  const raw = link.link_type === 'validity' ? link.payload?.valid_until : undefined;
  return typeof raw === 'string' ? formatDay(raw) : null;
}

/**
 * "Zeile 3" / "Line 3" for a line anchor (counted from 1 — `anchor_ref` "2"),
 * "Fakt" / "Fact" for a fact anchor — whose ref is a fact id, which means
 * nothing to a farmer — else `null`.
 */
export function anchorLabel(link: DocumentLink): string | null {
  if (link.anchor_kind === 'line') return i18n.t('twinLinks.details.line', { n: lineNumber(link.anchor_ref ?? '') });
  if (link.anchor_kind === 'fact') return i18n.t('twinLinks.details.fact');
  return null;
}

/** Everything that tells this row from a same-typed sibling: "Zeile 3", "4,20 €/kg", "bis 31.12.2027" (localized). */
export function linkDetails(link: DocumentLink): string[] {
  const out: string[] = [];
  const anchor = anchorLabel(link);
  if (anchor) out.push(anchor);
  const value = linkValueLabel(link);
  if (value) out.push(value);
  const until = validUntil(link);
  if (until) out.push(i18n.t('twinLinks.details.until', { date: until }));
  return out;
}
